import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { AppDownloadSource, type AppDownloadSourceValue } from '@/utils/urls/appDownloadRedirectUrl';

import { MOBILE_PHONE_SCREENSHOT_LEFT_URL, MOBILE_PHONE_SCREENSHOT_RIGHT_URL } from '../constants';

export type DownloadMobilePromoMobileProps = {
    className?: string;
    downloadUrl: string;
    onClose: () => void;
    onGetApp?: () => void;
    source?: AppDownloadSourceValue;
};

export const DownloadMobilePromoMobile = ({
    className,
    downloadUrl,
    onClose,
    onGetApp,
    source = AppDownloadSource.LeadingBidder,
}: DownloadMobilePromoMobileProps) => {
    const formatMessage = useAppFormatMessage();
    const titleKey = source === AppDownloadSource.SavedItem ? 'titleSavedItem' : 'title';

    return (
        <div
            className={cn(
                'flex w-[375px] max-w-full flex-col overflow-hidden rounded-lg bg-background-primary shadow-02',
                className
            )}
        >
            <div className="flex flex-col items-center gap-2xs rounded-t-lg bg-background-primary px-2xs pb-sm pt-2xs">
                <div className="flex w-full items-center justify-between">
                    <div
                        aria-hidden
                        className="min-h-[42px] min-w-0 flex-1 bg-background-primary"
                    />
                    <IconButton
                        /* As long as the translation key doesn't change, we can guarantee that this will be a string */
                        aria-label={String(formatMessage(['downloadMobilePromo', 'closeLabel']))}
                        className="shrink-0"
                        icon={Close}
                        iconStyle="bold"
                        onClick={() => {
                            onClose();
                        }}
                        size="md"
                        style="neutral"
                        variant="transparent"
                    />
                </div>
                <div className="flex w-full flex-col items-center gap-xs px-md text-center">
                    <H2 className="hui-text-h3 text-center tracking-[0.2px]">
                        {formatMessage(['downloadMobilePromo', titleKey])}
                    </H2>
                    <BodySecondary
                        className="text-center"
                        color="secondary"
                    >
                        {formatMessage(['downloadMobilePromo', 'subtitleMobile'])}
                    </BodySecondary>
                </div>
            </div>

            <div className="overflow-hidden bg-background-primary px-md py-sm">
                <div className="flex max-h-[320px] flex-col items-center justify-center gap-md overflow-hidden rounded-lg bg-background-tertiary py-md">
                    <div className="flex items-center justify-center pr-[48px]">
                        <div className="mr-[-48px] flex h-[282px] w-[172px] shrink-0 items-center justify-center">
                            <div className="-rotate-[10deg]">
                                <div className="relative h-[264px] w-32 overflow-hidden">
                                    <img
                                        alt=""
                                        className="absolute left-[-8%] top-[-1%] h-[101%] w-[112%] max-w-none"
                                        src={MOBILE_PHONE_SCREENSHOT_LEFT_URL}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mr-[-48px] flex h-[281px] w-[172px] shrink-0 items-center justify-center">
                            <div className="rotate-[10deg]">
                                <div className="relative h-[263px] w-32 overflow-hidden">
                                    <img
                                        alt=""
                                        className="absolute left-[-5%] top-0 h-[101%] w-[108%] max-w-none"
                                        src={MOBILE_PHONE_SCREENSHOT_RIGHT_URL}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center rounded-b-lg bg-background-primary px-md pb-lg pt-md">
                <Button
                    className="h-[42px] uppercase tracking-[1px]"
                    onClick={() => {
                        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                        onGetApp?.();
                    }}
                    size="md"
                    style="primary"
                    variant="filled"
                >
                    {formatMessage(['downloadMobilePromo', 'getTheApp'])}
                </Button>
            </div>
        </div>
    );
};
